import Button from "@lhd/components/Button";
import {
  FILTER_STATUS_TYPE,
  ROUTES,
  SCREEN_NAME,
  SETTING_CATEGORY_FROM,
  SPECIAL_FILTER_LABEL,
  SPECIAL_FILTER_VALUE,
} from "@lhd/constants";
import { isJapanese } from "@lhd/features/Apps/utils";
import { DISPLAY_SIZES } from "@lhd/types";
import { Tooltip } from "antd";
import { useMemo } from "react";
import { HiOutlineExternalLink } from "react-icons/hi";
import { useLocation } from "react-router-dom";
import { FilterSelected, Type } from "../../types";
import { Styles } from "./styled";
import CheckboxCustom from "@lhd/components/CheckboxCustom";

interface SelectFilterProps {
  title: string;
  labelButton: string;
  description: string;
  selectOptions: {
    text: string;
    value: string;
    is_hidden?: boolean;
    is_auto_select?: boolean;
    id?: any;
  }[];
  isCollapse?: boolean;
  setIsCollapse: (val: boolean) => void;
  listSelected: FilterSelected[];
  handleChange: (val: string, isChecked: boolean, type: Type) => void;
  handleChangeAll?: (isChecked: boolean, type?: Type) => void;
  disabled?: boolean;
  disabledButtonEdit?: boolean;
  isChangeSize?: boolean;
  displaySize?: any;
  height?: number;
  type: Type;
  screen?: string;
  showConfigControls?: boolean;
}

const SelectFilter = ({
  title,
  labelButton,
  description,
  selectOptions,
  isCollapse,
  listSelected,
  handleChange,
  handleChangeAll,
  setIsCollapse,
  disabled = false,
  disabledButtonEdit,
  type,
  displaySize,
  height,
  screen,
  showConfigControls = true,
}: SelectFilterProps) => {
  const selectOptionsFilter = selectOptions?.filter((i) => !i?.is_hidden);
  const location = useLocation();

  const fromParam = useMemo(() => {
    if (location.pathname.includes(ROUTES.WORKERS_CREATE))
      return SETTING_CATEGORY_FROM.WORKER_CREATE;
    if (location.pathname.includes(ROUTES.WORKERS))
      return SETTING_CATEGORY_FROM.WORKER_LIST;
    if (location.pathname.includes("/staff_tax_sheets"))
      return SETTING_CATEGORY_FROM.STAFF_TAX_SHEET;
    if (location.pathname.includes("/provisions"))
      return SETTING_CATEGORY_FROM.PROVISIONS;
    if (location.pathname.includes("/deductions"))
      return SETTING_CATEGORY_FROM.DEDUCTIONS;
    if (location.pathname.includes("/works"))
      return SETTING_CATEGORY_FROM.WORKS;
    if (location.pathname.includes("/expenses"))
      return SETTING_CATEGORY_FROM.EXPENSES;
    if (location.pathname.includes("/business_trip_reports"))
      return SETTING_CATEGORY_FROM.BUSINESS_TRIP_REPORTS;
    if (location.pathname.includes("/transfers"))
      return SETTING_CATEGORY_FROM.TRANSFER;
    if (location.pathname.includes("/arrangements"))
      return SETTING_CATEGORY_FROM.ARRANGEMENTS;
    if (location.pathname.includes("/salary_sheets"))
      return SETTING_CATEGORY_FROM.SALARY_SHEETS;
    if (location.pathname.includes("/payment_sheets"))
      return SETTING_CATEGORY_FROM.PAYMENT_SHEETS;
    if (location.pathname.includes(ROUTES.PROJECT_MANAGER))
      return SETTING_CATEGORY_FROM.PROJECT_MANAGER;
    if (location.pathname.includes(ROUTES.BUSINESS_FLOW))
      return SETTING_CATEGORY_FROM.BUSINESS_FLOWCHARTS;
    if (location.pathname.includes(ROUTES.BUSINESS_MANUALS))
      return SETTING_CATEGORY_FROM.BUSINESS_MANUALS;
    if (location.pathname.includes(ROUTES.COMPANY_POLICIES))
      return SETTING_CATEGORY_FROM.COMPANY_POLICIES;
    if (location.pathname.includes(ROUTES.CUSTOMER_LIST))
      return SETTING_CATEGORY_FROM.CLIENT;
    return "";
  }, [location.pathname]);

  const yearParam = useMemo(() => {
    const match = location.pathname.match(
      /\/annual_salary_summaries\/(\d{4})\//,
    );
    if (match) return match[1];
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("year") || "";
  }, [location.pathname, location.search]);

  const isLarge = displaySize === DISPLAY_SIZES.LARGE;
  const settingCategoryUrl = `/${ROUTES.SETTING_CATEGORIES}?type=${type}${
    screen ? `&screen=${screen}` : ""
  }&isDisplayInNewTab=true${fromParam ? `&from=${fromParam}` : ""}${
    yearParam ? `&year=${yearParam}` : ""
  }`;
  // Check if all non-null visible options are checked
  const isAllOtherChecked = useMemo(() => {
    const nonNullOptions = selectOptionsFilter?.filter(
      (opt) =>
        opt?.value !== SPECIAL_FILTER_VALUE.NULL &&
        opt?.text !== SPECIAL_FILTER_LABEL.NULL,
    );
    if (!nonNullOptions || nonNullOptions.length === 0) return false;
    return nonNullOptions.every((optionItem) => {
      const itemSelected = listSelected.find(
        (x) =>
          x.value === optionItem?.value ||
          String(x.value) === String(optionItem?.value) ||
          String(x.label) === String(optionItem?.text) ||
          String(x.text) === String(optionItem?.text),
      );
      return (
        itemSelected?.status === FILTER_STATUS_TYPE.CHECKED ||
        itemSelected?.status === FILTER_STATUS_TYPE.SELECTED
      );
    });
  }, [selectOptionsFilter, listSelected]);

  const isCheckAll = useMemo(() => {
    if (!isAllOtherChecked) return false;
    const nullOption = selectOptionsFilter?.find(
      (opt) =>
        opt?.value === SPECIAL_FILTER_VALUE.NULL ||
        opt?.text === SPECIAL_FILTER_LABEL.NULL,
    );
    if (!nullOption) return true;

    const nullSelected = listSelected.find(
      (x) =>
        (nullOption?.value &&
          (x.value === nullOption.value ||
            String(x.value) === String(nullOption.value))) ||
        x.value === SPECIAL_FILTER_VALUE.NULL ||
        x.text === SPECIAL_FILTER_LABEL.NULL ||
        x.label === SPECIAL_FILTER_LABEL.NULL,
    );
    if (!nullSelected) return false;
    return (
      nullSelected.status === FILTER_STATUS_TYPE.CHECKED ||
      nullSelected.status === FILTER_STATUS_TYPE.SELECTED
    );
  }, [isAllOtherChecked, selectOptionsFilter, listSelected]);
  const optionItem = {
    text: SPECIAL_FILTER_LABEL.ALL,
    value: SPECIAL_FILTER_VALUE.ALL,
  };

  return (
    <Styles
      text_scale={displaySize}
      height={displaySize === DISPLAY_SIZES.SMALL ? 20 : height}
    >
      {isCollapse && (
        <div className="section-header">
          <div
            className="d-flex justify-between"
            style={{
              flexDirection: isLarge ? "column" : "row",
              alignItems: isLarge ? "flex-start" : "center",
            }}
          >
            <h3 className="title-select-filter">{title}</h3>
            {showConfigControls && (
              <Button
                label={
                  <a
                    href={
                      screen === SCREEN_NAME.LIST_MESS ||
                      screen === SCREEN_NAME.CHAT
                        ? `/${ROUTES.SETTING_CATEGORIES}?type=${type}&screen=${SCREEN_NAME.CHAT}&isDisplayInNewTab=true`
                        : settingCategoryUrl
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`btn-a-link ${
                      disabledButtonEdit ? "btn-a-link-disabled" : ""
                    }`}
                  >
                    {labelButton}
                  </a>
                }
                icon={
                  <HiOutlineExternalLink
                    size={
                      displaySize === DISPLAY_SIZES.SMALL
                        ? 14
                        : displaySize === DISPLAY_SIZES.MEDIUM
                        ? 16
                        : 20
                    }
                  />
                }
                iconRight
                className="button-edit-add"
                // type="default"
                disabled={disabledButtonEdit}
              />
            )}
          </div>

          <div className="select-all-row d-flex justify-between align-item-center">
            <div style={{ maxWidth: 120 }}>
              <div
                key={SPECIAL_FILTER_VALUE.ALL}
                className={`select-all-filter-item ${
                  isCheckAll ? "select-filter-item-active" : ""
                }`}
                onClick={() =>
                  isCollapse
                    ? handleChangeAll?.(!isCheckAll, type)
                    : setIsCollapse(true)
                }
              >
                <div
                  style={{ paddingBlock: 1, marginTop: 4 }}
                  className="d-flex align-items-center gap-2 "
                >
                  {isCollapse && (
                    <CheckboxCustom
                      checked={isCheckAll}
                      disabled={disabled}
                      className="select-filter-checkbox"
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) =>
                        handleChangeAll?.(e.target.checked, type)
                      }
                      displaySize={displaySize}
                    />
                  )}
                  <div
                    className={`select-filter-item-label ${
                      isCheckAll ? "select-filter-item-label-active" : ""
                    }`}
                  >
                    {isCollapse ? (
                      "全選択"
                    ) : (
                      <Tooltip title={optionItem?.text}>全選択</Tooltip>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="section-description">
              <p className="text-label">抽出列</p>
              {showConfigControls && (
                <div className="box-description">{description}</div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className={
          "wrap-list-option " + (isCollapse ? "box-expend" : "box-collapse")
        }
      >
        {selectOptionsFilter?.map((optionItem) => {
          const maxLength = isJapanese(String(optionItem?.text || "")) ? 9 : 18;
          const isLongContent =
            optionItem?.text &&
            String(optionItem?.text || "")?.length > maxLength;
          const isNullOption =
            optionItem?.value === SPECIAL_FILTER_VALUE.NULL ||
            optionItem?.text === SPECIAL_FILTER_LABEL.NULL;

          const itemSelected = listSelected.find(
            (x) =>
              x.value === optionItem?.value ||
              String(x.value) === String(optionItem?.value) ||
              String(x.label) === String(optionItem?.text) ||
              String(x.text) === String(optionItem?.text) ||
              (isNullOption &&
                (x.value === SPECIAL_FILTER_VALUE.NULL ||
                  x.text === SPECIAL_FILTER_LABEL.NULL ||
                  x.label === SPECIAL_FILTER_LABEL.NULL)),
          );

          const isChecked =
            itemSelected?.status === FILTER_STATUS_TYPE.CHECKED ||
            itemSelected?.status === FILTER_STATUS_TYPE.SELECTED;

          const displayText = isCollapse
            ? isLongContent
              ? optionItem?.text?.slice(0, maxLength) + "..."
              : optionItem?.text
            : optionItem?.text?.length > 1
            ? optionItem?.text?.slice(0, 1) + "..."
            : optionItem?.text;

          const labelContent = isLongContent ? (
            <Tooltip title={optionItem?.text}>{displayText}</Tooltip>
          ) : (
            displayText
          );

          return (
            <div
              key={`${optionItem?.text}_${optionItem?.id}`}
              className={`select-filter-item ${
                isChecked ? "select-filter-item-active" : ""
              }`}
              onClick={() =>
                isCollapse
                  ? handleChange(optionItem?.value, !isChecked, type)
                  : setIsCollapse(true)
              }
            >
              <div
                className="d-flex align-items-center gap-2"
                style={{
                  minHeight: 17,
                  paddingBlock: 1,
                }}
              >
                {isCollapse && (
                  <CheckboxCustom
                    checked={isChecked}
                    disabled={disabled}
                    className="select-filter-checkbox"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) =>
                      handleChange(optionItem?.value, e.target.checked, type)
                    }
                    displaySize={displaySize}
                  />
                )}
                <div
                  className={`select-filter-item-label ${
                    isChecked ? "select-filter-item-label-active" : ""
                  }`}
                >
                  {isCollapse ? (
                    labelContent
                  ) : (
                    <Tooltip title={optionItem?.text}>{labelContent}</Tooltip>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Styles>
  );
};

export default SelectFilter;
